import Alpine from 'alpinejs';

const emptyFormData =  () => ({
    search: '',
    winnersOnly: false,
    nomineeName: '',
    tvProgramTitle: '',
    yearMin: '',
    yearMax: '',
});

Alpine.data('awardsSearch', () => ({
    awardsCategoryOptions: [],
    tvNetworkOptions: [],
    productionCompanyOptions: [],
    isAdvancedSearch: false,

    formData: emptyFormData(),

    showResults: false,
    showResetButton: false,
    searchResults: [],
    pagination: [],
    totalHits: null,
    firstHit: null,
    lastHit: null,

    initialPage: 1,
    initialFilters: {}, // Can't set dropdown values before they're loaded with shoelace. Storing them here for initialization.
    initialized: false,

    currentUrlHash: '',
    multiple: true,
    value: [],
    valueNetwork: [],
    valueAwardsCategory: [],
    options: [],

    hasSearchState() {
        return !!(
            this.formData.search ||
            this.formData.winnersOnly ||
            this.formData.nomineeName ||
            this.formData.tvProgramTitle ||
            this.initialFilters.categories?.length ||
            this.initialFilters.tvNetworks?.length ||
            this.initialFilters.productionCompanies?.length
        );
    },

    async init() {
        this.currentUrlHash = window.location.hash || '#';

        await this.pullStateFromUrl();
        await Promise.all([
            this.hasSearchState() ? this.fetchResults(this.initialPage) : Promise.resolve(),
            this.fetchDropdownOptions(),
        ]);

        let minRangeValueGap = 0;
        const range = document.getElementById("range_track");
        const minval = document.querySelector(".minvalue");
        const maxval = document.querySelector(".maxvalue");
        const rangeInput = document.querySelectorAll(".form-group-range-slider input");

        let minRange, maxRange;

        const minRangeFill = () => {
            range.style.left = 100 - ((rangeInput[1].max - rangeInput[0].value) / (rangeInput[1].max - rangeInput[1].min)) * 100 + "%";
        };
        const maxRangeFill = () => {
            range.style.right = 100 - ((rangeInput[1].value - rangeInput[0].min) / (rangeInput[1].max - rangeInput[1].min)) * 100 + "%";
        };

        const setMinValueOutput = () => {
            minRange = parseInt(rangeInput[0].value);
            minval.innerHTML = rangeInput[0].value;
        };
        const setMaxValueOutput = () => {
            maxRange = parseInt(rangeInput[1].value);
            maxval.innerHTML = rangeInput[1].value;
        };

        setMinValueOutput();
        setMaxValueOutput();
        minRangeFill();
        maxRangeFill();

        rangeInput.forEach((input) => {
            input.addEventListener("input", (e) => {
                setMinValueOutput();
                setMaxValueOutput();

                minRangeFill();
                maxRangeFill();

                if (maxRange - minRange < minRangeValueGap) {
                    if (e.target.className === "min") {
                        rangeInput[0].value = maxRange - minRangeValueGap;
                        setMinValueOutput();
                        minRangeFill();
                        e.target.style.zIndex = "2";
                    } else {
                        rangeInput[1].value = minRange + minRangeValueGap;
                        e.target.style.zIndex = "2";
                        setMaxValueOutput();
                        maxRangeFill();
                    }
                }
            });
        });
    },

    //
    // Store and retrieve search state from URL.
    //  using hash instead of query string as advanced filters can get really long and the server does not need
    //  the parameters on the GET request.
    //
    async pullStateFromUrl() {
        const hashString = window.location.hash || '#?';
        if (!hashString.startsWith('#?')) {
            return;
        }
        const urlParams = new URLSearchParams(hashString.substring(2));
        this.formData.search = urlParams.get('q') || '';
        this.formData.winnersOnly = urlParams.get('wo') === '1';

        this.isAdvancedSearch = urlParams.get('adv') === '1';
        this.formData.nomineeName = urlParams.get('nominee') || '';
        this.formData.tvProgramTitle = urlParams.get('tvProgram') || '';

        const filters = await this.parseAdvancedFiltersFromUrlParam(urlParams.get('filters') || '');
        this.initialFilters.categories = filters.categories ? filters.categories : [];
        this.initialFilters.tvNetworks = filters.tvNetworks ? filters.tvNetworks : [];
        this.initialFilters.productionCompanies = filters.productionCompanies ? filters.productionCompanies : [];
        this.initialFilters.yearMin = filters.yearMin || this.$refs.yearMin.min;
        this.initialFilters.yearMax = filters.yearMax || this.$refs.yearMax.max;

        if (this.initialized) {
            this.$refs.awardsCategorySelect.value = this.initialFilters.categories || [];
            this.$refs.tvNetworksSelect.value = this.initialFilters.tvNetworks || [];
            this.$refs.productionCompaniesSelect.value = this.initialFilters.productionCompanies || [];
            this.$refs.yearMin.value = this.initialFilters.yearMin || this.$refs.yearMin.min;
            this.$refs.yearMax.value = this.initialFilters.yearMax || this.$refs.yearMax.max;
        }
        this.initialPage = !isNaN(urlParams.get('p'))  && urlParams.get('p') > 0 ? urlParams.get('p') : 1;
    },

    async pushStateToUrl(searchParams, page, isAdvancedSearch) {
        const urlParams = {
            q: searchParams.search,
            wo: searchParams.winnersOnly ? '1' : '',
            p: page > 1 ? page : '',

            ...(isAdvancedSearch ? {
                    adv: '1',
                    nominee: searchParams.nomineeName,
                    tvProgram: searchParams.tvProgramTitle,
                    filters: await this.mapAdvancedDropdownFiltersForUrlParam(searchParams),
                }
                : {}),
        };

        for (const key in urlParams) {
            if (!urlParams[key]) {
                delete urlParams[key];
            }
        }

        if (Object.keys(urlParams).length === 0) {
            this.updateUrlHashSilently('');
            return;
        }

        this.updateUrlHashSilently('?' + new URLSearchParams(urlParams).toString());
    },

    async parseAdvancedFiltersFromUrlParam(filterString) {
        const decompressed = await this.decompressParam(filterString);

        if (!decompressed) {
            return {};
        }

        try {
            return JSON.parse(decompressed);
        } catch (e) {
            console.error('Error parsing advanced filters:', e);
            return {};
        }
    },

    async mapAdvancedDropdownFiltersForUrlParam(searchParams) {
        const filters = {
            categories: this.valueAwardsCategory,
            tvNetworks: this.valueNetwork,
            productionCompanies: this.value,
            yearMin: this.$refs.yearMin.value,
            yearMax: this.$refs.yearMax.value,
        };

        for (const key in filters) {
            if (!filters[key].length) {
                delete filters[key];
            }
        }

        if (!Object.keys(filters).length) {
            return '';
        }

        return await this.compressParam(JSON.stringify(filters));
    },

    async fetchDropdownOptions() {
        const response = await fetch('/api/awards-search/dropdown-options');
        const data = await response.json();
        this.awardsCategoryOptions = data.awardsCategories;
        this.tvNetworkOptions = data.tvNetworks;
        this.productionCompanyOptions = data.productionCompanies;
        this.$refs.awardsCategorySelect.value = [this.initialFilters.categories] || [];
        this.$refs.tvNetworksSelect.value = [this.initialFilters.tvNetworks] || [];
        this.$refs.productionCompaniesSelect.value = [this.initialFilters.productionCompanies] || [];
        this.value = this.initialFilters.productionCompanies || [];
        this.valueNetwork = this.initialFilters.tvNetworks || [];
        this.valueAwardsCategory = this.initialFilters.categories || [];
        this.$refs.yearMin.value = this.initialFilters.yearMin || [];
        this.$refs.yearMax.value = this.initialFilters.yearMax || [];
        this.createSelect2();
        this.initialized = true;
    },
    refreshSelect2(){
        window.jQuery(this.$refs.productionCompaniesSelect).select2('destroy')
        this.$refs.productionCompaniesSelect.innerHTML = ''
        window.jQuery(this.$refs.tvNetworksSelect).select2('destroy')
        this.$refs.tvNetworksSelect.innerHTML = ''
        window.jQuery(this.$refs.awardsCategorySelect).select2('destroy')
        this.$refs.awardsCategorySelect.innerHTML = ''
        this.bootSelect2()
    },
    resetForm() {
        window.scrollTo(0, 0);
        window.location.href = '/awards/awards-search#?adv=1'
        window.location.reload();
    },

    async submitSearch() {
        await this.fetchResults();
    },

    async fetchResults(page = 1, scroll = false) {
        // if valueAwardsCategory, valueNetwork or value is empty, set it to the initialFilters
        if ((this.valueAwardsCategory.length === 0) && (this.valueNetwork.length === 0) && (this.value.length === 0)) {
            this.showResetButton = false;
        } else {
            this.showResetButton = true;
        }

        const searchParameters = this.isAdvancedSearch
            ? { ...this.formData,
                categories: this.initialized ? this.valueAwardsCategory : (this.initialFilters.categories || []),
                tvNetworks: this.initialized ? this.valueNetwork : (this.initialFilters.tvNetworks || []),
                productionCompanies: this.initialized ? this.value : (this.initialFilters.productionCompanies || []),
                yearMin: this.initialized ? this.$refs.yearMin.value : (this.initialFilters.yearMin || this.$refs.yearMin.min),
                yearMax: this.initialized ? this.$refs.yearMax.value : (this.initialFilters.yearMax || this.$refs.yearMax.max),
            }
            : { search: this.formData.search, winnersOnly: this.formData.winnersOnly };
        const response = await fetch('/api/awards-search', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...searchParameters,
                page,
                baseUrl: window.location.pathname + '?' + new URLSearchParams(searchParameters).toString(),
            }),
        });

        if (this.initialized) {
            await this.pushStateToUrl(searchParameters, page, this.isAdvancedSearch);
        }
        const data = await response.json();
        this.searchResults = data.results;
        this.totalHits = data.totalHits;
        this.firstHit = data.firstHit;
        this.lastHit = data.lastHit
        this.pagination = data.pagination;
        this.showResults = true;

        // determine if formData is empty
        for (const key in this.formData) {
            if (this.formData[key]) {
                this.showResetButton = true;
            }
        }



        if (scroll && document.getElementById('awards-search-pagination-scroll-target')) {
            document.getElementById('awards-search-pagination-scroll-target').scrollIntoView({ behavior: 'smooth' });
        }
    },


    changePage(page) {
        if (page === null) {
            return;
        }

        this.fetchResults(page, true);
    },

    updateUrlHashSilently(newHash) {
        if (typeof newHash !== 'string') {
            return;
        }
        if (!newHash.startsWith('#')) {
            newHash = '#' + newHash;
        }

        this.currentUrlHash = newHash;

        window.history.replaceState(null, null, this.currentUrlHash === '#' ? window.location.pathname : this.currentUrlHash);
    },

    async compressParam(value) {
        if (!value) {
            return '';
        }

        if (!'CompressionStream' in window) {
            return value;
        }

        const compressedStream1 = new Blob([value])
            .stream()
            .pipeThrough(new window.CompressionStream('gzip'));

        const bytes = new Uint8Array(await new Response(compressedStream1).arrayBuffer())
            .reduce((data, byte) => data + String.fromCharCode(byte), '');

        return 'gz:' + window.btoa(bytes);
    },

    async decompressParam(value) {
        if (!value) {
            return '';
        }

        if (!value.startsWith('gz:')) {
            return value;
        }

        if (!'DecompressionStream' in window) {
            return '';
        }

        const base64 = value.substring(3);
        const bytes = new Uint8Array(window.atob(base64).split('').map(char => char.charCodeAt(0)));

        const decompressedStreamReader = new Blob([bytes])
            .stream()
            .pipeThrough(new window.DecompressionStream('gzip'))

         return await new Response(await decompressedStreamReader).text();
    },
    createSelect2() {
        this.bootSelect2()
        window.jQuery(this.$refs.productionCompaniesSelect).on('change', () => {
            let currentSelection = window.jQuery(this.$refs.productionCompaniesSelect).select2('data')
            this.value = this.multiple
                ? currentSelection.map(i => i.id)
                : currentSelection[0].id
        })
        window.jQuery(this.$refs.tvNetworksSelect).on('change', () => {
            let currentSelectionNetwork = window.jQuery(this.$refs.tvNetworksSelect).select2('data')

            this.valueNetwork = this.multiple
                ? currentSelectionNetwork.map(i => i.id)
                : currentSelectionNetwork[0].id
        })

        window.jQuery(this.$refs.awardsCategorySelect).on('change', () => {
            let currentSelectionAwardsCategory = window.jQuery(this.$refs.awardsCategorySelect).select2('data')
            this.valueAwardsCategory = this.multiple
                ? currentSelectionAwardsCategory.map(i => i.id)
                : currentSelectionAwardsCategory[0].id
        })

        window.jQuery('label.category-label').on('click', () => {
            window.jQuery(this.$refs.awardsCategorySelect).select2('open');
        })
        window.jQuery('label.network-label').on('click', () => {
            window.jQuery(this.$refs.tvNetworksSelect).select2('open');
        })
        window.jQuery('label.production-label').on('click', () => {
            window.jQuery(this.$refs.productionCompaniesSelect).select2('open');
        })


    },
    bootSelect2() {
        let selections = this.multiple ? this.value : [this.value]
        window.jQuery(this.$refs.productionCompaniesSelect).select2({
            multiple: this.multiple,
            data: this.productionCompanyOptions.map(i => ({
                id: i.id,
                text: i.label,
                selected: selections.map(j => String(j)).includes(String(i.id)),
            })),
        })

        let selectionsNetwork = this.multiple ? this.valueNetwork : [this.valueNetwork]
        window.jQuery(this.$refs.tvNetworksSelect).select2({
            multiple: this.multiple,
            data: this.tvNetworkOptions.map(i => ({
                id: i.id,
                text: i.label,
                selected: selectionsNetwork.map(j => String(j)).includes(String(i.id)),
            })),
        })

        let selectionsCategory = this.multiple ? this.valueAwardsCategory : [this.valueAwardsCategory]
        window.jQuery(this.$refs.awardsCategorySelect).select2({
            multiple: this.multiple,
            data: this.awardsCategoryOptions.map(i => ({
                id: i.id,
                text: i.label,
                selected: selectionsCategory.map(j => String(j)).includes(String(i.id)),
            })),
        })

    },
    changeTabs: function(tabName) {
        if(tabName == 'advanced') {
            this.isAdvancedSearch = true;
            this.updateUrlHashSilently('#?adv=1');
        } else {
            this.isAdvancedSearch = false;
        }
    },
}));
